<script setup lang="ts">
import { ComponentPublicInstance, computed, ref } from 'vue'
import { useStore } from 'vuex'
import { formatDate } from '@/utils/dateFormatter'
import BaseModal from '@/components/BaseModal.vue'
import FileInput from '@/components/FileInput.vue'
import { x509Certificate, GlobalMethods } from 'e.firma'

const showModalDownloadTemplate = ref(false)
const selectedFile = ref<File | null>(null)
const fileBase64 = ref<string>('')
const isUploading = ref(false)
const uploadError = ref<string>('')
const store = useStore()
let userData = ref(store.state.global.user)
const certificado = ref<x509Certificate | null>(null)
const archivos = ref<ArchivosFirma>({})
const showSuccessModal = ref(false)
const successMessage = ref<string>('')
const refs = {
	certificate: ref<HTMLInputElement | null>(null)
}
const certificateCurp = ref<string>('')
interface ArchivosFirma {
	certificate?: File
}
const fileInputs = {
	certificate: {
		label: 'Certificado (.cer)',
		accept: '.cer',
		icon: 'fa-certificate'
	}
}
// Función para obtener las iniciales del usuario
const avatarLetter = computed(() => {
	if (!userData.value?.me?.nombre || !userData.value?.me?.primerApellido) return 'U'
	const firstName = userData.value.me.nombre.charAt(0).toUpperCase()
	const lastName = userData.value.me.primerApellido.charAt(0).toUpperCase()
	return `${firstName}${lastName}`
})
const isNearExpiry = computed(() => {
	if (!hasValidCertificate.value || !userData.value?.me?.cer?.x509?.notAfter) return false
	const expiryDate = new Date(userData.value.me.cer.x509.notAfter)
	const today = new Date()
	const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
	return daysUntilExpiry <= 30 && daysUntilExpiry > 0
})

const daysUntilExpiry = computed(() => {
	if (!hasValidCertificate.value || !userData.value?.me?.cer?.x509?.notAfter) return 0
	const expiryDate = new Date(userData.value.me.cer.x509.notAfter)
	const today = new Date()
	return Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
})

const isExpired = computed(() => {
	if (!hasValidCertificate.value || !userData.value?.me?.cer?.x509?.notAfter) return false
	const expiryDate = new Date(userData.value.me.cer.x509.notAfter)
	const today = new Date()
	return expiryDate < today
})

// Función para verificar si el certificado tiene datos válidos
const hasValidCertificate = computed(() => {
	const cert = userData.value?.me?.cer
	if (!cert) return false

	// Verificar si tiene datos básicos del certificado
	const hasBasicData = cert.certificadoResponsable && cert.noCertificadoResponsable && cert.pem

	// Verificar si tiene datos X509 válidos
	const x509 = cert.x509
	const hasX509Data = x509 && x509.notAfter && x509.notBefore && x509.serial && x509.subject?.commonName

	return hasBasicData && hasX509Data
})

const renewCertificate = () => {
	showModalDownloadTemplate.value = true
}

// Función para procesar el certificado
const processCertificate = async () => {
	if (!selectedFile.value) {
		uploadError.value = 'Por favor selecciona un archivo'
		return
	}
	if (userData.value.me?.curp !== undefined && userData.value.me?.curp !== null && userData.value.me?.curp !== '') {
		if (certificateCurp.value !== userData.value.me?.curp) {
			uploadError.value = 'El CURP del certificado no coincide con el CURP del usuario'
			return
		}
	}
	isUploading.value = true
	fileBase64.value = await getFile(selectedFile.value).then(base64 => {
		setTimeout(() => {
			isUploading.value = false
		}, 1000)
		return base64
	})
	// Aquí puedes agregar la lógica para procesar el certificado
	store.dispatch('updateCertificate', { certificate: fileBase64.value })
	// Cerrar el modal
	showModalDownloadTemplate.value = false
	// Limpiar el formulario
	selectedFile.value = null
	fileBase64.value = ''
	uploadError.value = ''

	// Mostrar modal de confirmación y recargar página
	setTimeout(() => {
		showSuccessModal.value = true
		successMessage.value = 'Certificado procesado exitosamente. La página se recargará para mostrar los cambios.'
		window.location.reload()
	}, 1000)
}

// Función para cancelar
const cancelUpload = () => {
	showModalDownloadTemplate.value = false
	selectedFile.value = null
	fileBase64.value = ''
	uploadError.value = ''
	certificado.value = null
}
const setFileRef = (el: Element | ComponentPublicInstance | null, tipo: keyof typeof refs) => {
	if (el instanceof HTMLInputElement || el === null) {
		refs[tipo].value = el
	}
}

const onFileSelected = async (event: Event): Promise<void> => {
	const input = event.target as HTMLInputElement
	if (input.files?.[0]) {
		selectedFile.value = input.files[0]
	}

	// Validar que el archivo sea un certificado .cer
	if (!selectedFile.value?.name.toLowerCase().endsWith('.cer')) {
		uploadError.value = 'Por favor selecciona un archivo .cer válido'
		selectedFile.value = null
		return
	}

	try {
		// Leer el archivo como base64
		const fileBase64 = await getFile(selectedFile.value)

		// Convertir de base64 a binario usando GlobalMethods
		const certificadoBinary = GlobalMethods.base64ToBinary(fileBase64)

		// Crear instancia del certificado X.509 para validar que sea válido
		certificado.value = new x509Certificate(certificadoBinary)

		if (certificado.value.certificate.validity?.notAfter < new Date()) {
			uploadError.value = 'El certificado ha expirado'
			selectedFile.value = null
			return
		}
		// Si llegamos aquí, el certificado es válido
		console.log('Certificado válido cargado:', certificado)
		certificateCurp.value = certificado.value.certificate.subject.attributes[6].value
		// Limpiar cualquier error previo
		uploadError.value = ''

		// Aquí podrías agregar lógica adicional para validar el certificado
		// Por ejemplo, verificar que sea un certificado de e.firma válido
	} catch (error) {
		console.error('Error al leer el certificado:', error)
		uploadError.value = 'Error al leer el archivo del certificado. Asegúrate de que sea un archivo .cer válido.'
		selectedFile.value = null
	}
}
const getFile = async (file: File): Promise<string> =>
	new Promise<string>((resolve, reject) => {
		const reader = new FileReader()

		reader.onload = () => {
			if (!reader.result) {
				reject(new Error('No se pudo leer el archivo'))
				return
			}

			if (typeof reader.result === 'string') {
				const base64 = reader.result.split(',')[1]
				resolve(base64)
			} else {
				const uint8Array = new Uint8Array(reader.result)
				const textDecoder = new TextDecoder('utf-8')
				resolve(textDecoder.decode(uint8Array))
			}
		}

		reader.onerror = () => {
			reject(new Error('Error al leer el archivo: ' + (reader.error?.message || 'Error desconocido')))
		}

		reader.readAsDataURL(file)
	})
</script>
<template>
	<!-- Header con gradiente moderno -->
	<VaCard class="header-card" flat>
		<VaCardTitle>
			<div class="header-content">
				<div class="header-title">
					<div class="title-wrapper">
						<VaIcon name="account_circle" class="header-icon" />
						<span class="institution-title">Perfil de Usuario</span>
					</div>
					<div class="header-subtitle">Gestiona tu información personal y certificado digital</div>
				</div>
			</div>
		</VaCardTitle>
		<VaCardContent>
			<div class="user-profile-container">
				<div class="row">
					<div class="col-md-6">
						<!-- Avatar y información principal con diseño mejorado -->
						<VaCard class="user-profile-card" flat>
							<VaCardContent class="user-profile-content">
								<div class="avatar-section">
									<div class="avatar-wrapper">
										<VaAvatar size="large" class="user-avatar" :icon="avatarLetter" />
										<div class="avatar-status-indicator" :class="{ 'online': hasValidCertificate && !isExpired }"></div>
									</div>
									<div class="user-name-section">
										<h3 class="user-full-name">
											{{ userData.me?.cer.x509.subject.commonName }}
										</h3>
										<p class="user-email">
											<VaIcon name="email" class="email-icon" />
											{{ userData.me?.email }}
										</p>
										<div class="user-role">
											<VaIcon name="verified_user" class="role-icon" />
											<span>Usuario Verificado</span>
										</div>
									</div>
								</div>
							</VaCardContent>
						</VaCard>
					</div>
					<div class="col-md-6">
						<!-- Alerta de certificado expirado -->
						<VaCard v-if="isExpired" class="expiry-alert expired" flat>
							<VaCardContent class="alert-content">
								<div class="alert-icon-wrapper">
									<VaIcon name="error" size="large" class="alert-icon" />
								</div>
								<div class="alert-text">
									<h4 class="alert-title">Certificado Expirado</h4>
									<p class="alert-message">Tu certificado digital expiró el {{ formatDate(userData.me?.cer?.x509?.notAfter) }}. Necesitas renovarlo para continuar usando el sistema.</p>
								</div>
								<div class="alert-action">
									<VaButton color="danger" size="small" class="action-button" @click="renewCertificate">
										<VaIcon name="refresh" class="me-2" />
										Renovar Certificado
									</VaButton>
								</div>
							</VaCardContent>
						</VaCard>

						<!-- Alerta de certificado próximo a expirar -->
						<VaCard v-else-if="isNearExpiry" class="expiry-alert warning" flat>
							<VaCardContent class="alert-content">
								<div class="alert-icon-wrapper">
									<VaIcon name="warning" size="large" class="alert-icon" />
								</div>
								<div class="alert-text">
									<h4 class="alert-title">Certificado Próximo a Expirar</h4>
									<p class="alert-message">
										Tu certificado digital expirará en <strong>{{ daysUntilExpiry }} días</strong> (el {{ formatDate(userData.me?.cer?.x509?.notAfter) }}).
									</p>
								</div>
								<div class="alert-action">
									<VaButton color="warning" size="small" class="action-button" @click="renewCertificate">
										<VaIcon name="refresh" class="me-2" />
										Renovar Ahora
									</VaButton>
								</div>
							</VaCardContent>
						</VaCard>
						<!-- Alerta de certificado no existente -->
						<VaCard v-else-if="!hasValidCertificate" class="expiry-alert warning" flat>
							<VaCardContent class="alert-content">
								<div class="alert-icon-wrapper">
									<VaIcon name="gpp_bad" size="large" class="alert-icon" />
								</div>
								<div class="alert-text">
									<h4 class="alert-title">Sin Certificado Digital Válido</h4>
									<p class="alert-message">No tienes un certificado digital válido registrado. Necesitas uno para poder utilizar el sistema.</p>
								</div>
								<div class="alert-action">
									<VaButton color="primary" size="small" class="action-button" @click="renewCertificate">
										<VaIcon name="add" class="me-2" />
										Actualizar Certificado
									</VaButton>
								</div>
							</VaCardContent>
						</VaCard>

						<!-- Alerta de certificado válido -->
						<VaCard v-else class="expiry-alert valid" flat>
							<VaCardContent class="alert-content">
								<div class="alert-icon-wrapper">
									<VaIcon name="verified" size="large" class="alert-icon" />
								</div>
								<div class="alert-text">
									<h4 class="alert-title">Certificado Válido</h4>
									<p class="alert-message">
										Tu certificado digital es válido hasta el {{ formatDate(userData.me?.cer?.x509?.notAfter) }}. <strong>{{ daysUntilExpiry }} días restantes</strong>.
									</p>
								</div>
								<div class="alert-action">
									<VaButton color="primary" size="small" class="action-button" @click="renewCertificate">
										<VaIcon name="add" class="me-2" />
										Actualizar Certificado
									</VaButton>
								</div>
							</VaCardContent>
						</VaCard>
					</div>
				</div>

				<!-- Información detallada del usuario con diseño mejorado -->
				<VaCard class="user-details-card" flat>
					<VaCardTitle class="card-title">
						<div class="title-content">
							<VaIcon name="person" class="title-icon" />
							<span>Información Personal</span>
						</div>
					</VaCardTitle>
					<VaCardContent>
						<div class="details-grid">
							<div class="detail-item">
								<div class="detail-icon-wrapper">
									<VaIcon name="badge" class="detail-icon" />
								</div>
								<div class="detail-content">
									<label class="detail-label" for="curp">CURP</label>
									<span class="detail-value">{{ userData.me?.curp || 'No especificado' }}</span>
								</div>
							</div>
							<div class="detail-item">
								<div class="detail-icon-wrapper">
									<VaIcon name="phone" class="detail-icon" />
								</div>
								<div class="detail-content">
									<label class="detail-label" for="phone">Teléfono</label>
									<span class="detail-value">{{ userData.me?.phone || 'No especificado' }}</span>
								</div>
							</div>
						</div>
					</VaCardContent>
				</VaCard>

				<!-- Información del certificado con diseño mejorado -->
				<VaCard v-if="hasValidCertificate" class="certificate-card" flat>
					<VaCardTitle class="card-title">
						<div class="title-content">
							<VaIcon name="verified" class="title-icon" />
							<span>Certificado Digital</span>
						</div>
					</VaCardTitle>
					<VaCardContent>
						<div class="certificate-info">
							<div class="certificate-item">
								<div class="certificate-icon-wrapper">
									<VaIcon name="description" class="certificate-icon" />
								</div>
								<div class="certificate-content">
									<label class="certificate-label" for="certificate-name">Nombre del certificado</label>
									<span class="certificate-value">{{ userData.me?.cer?.x509?.subject?.commonName || undefined }}</span>
								</div>
							</div>
							<div class="certificate-item">
								<div class="certificate-icon-wrapper">
									<VaIcon name="fingerprint" class="certificate-icon" />
								</div>
								<div class="certificate-content">
									<label class="certificate-label" for="certificate-serial">Número de serie</label>
									<span class="certificate-value">{{ userData.me?.cer?.x509?.serial || undefined }}</span>
								</div>
							</div>
							<div class="certificate-item">
								<div class="certificate-icon-wrapper">
									<VaIcon name="event" class="certificate-icon" />
								</div>
								<div class="certificate-content">
									<label class="certificate-label" for="certificate-notBefore">Fecha de emisión</label>
									<span class="certificate-value">{{ formatDate(userData.me?.cer?.x509?.notBefore) || undefined }}</span>
								</div>
							</div>
						</div>
					</VaCardContent>
				</VaCard>
			</div>
		</VaCardContent>
	</VaCard>

	<BaseModal v-model="showModalDownloadTemplate" blur title="Subir Certificado Digital (.cer)" hideDefaultActions message="Subir Certificado Digital (.cer)" :testMode="false" size="medium">
		<template #body>
			<VaCard class="modal-card">
				<div class="upload-container">
					<!-- File Input -->
					<div class="file-input-section">
						<FileInput
							v-for="(config, tipo) in fileInputs"
							:key="tipo"
							:id="tipo"
							:label="config.label"
							:accept="config.accept"
							:icon="config.icon"
							:value="archivos[tipo] || null"
							@change="(event: Event) => onFileSelected(event)"
							:ref="(el: Element | ComponentPublicInstance | null) => setFileRef(el, tipo)" />
						<div v-if="uploadError" class="va-input__error">{{ uploadError }}</div>
						<div v-if="selectedFile" class="file-info">
							<VaIcon name="description" class="file-icon" />
							<span>{{ selectedFile.name }} ({{ (selectedFile.size / 1024).toFixed(2) }} KB)</span>
						</div>

						<VaCard class="certificate-card" flat style="margin-top: 10px">
							<VaCardTitle class="card-title">
								<VaIcon name="verified" class="title-icon" />
								Certificado digital
							</VaCardTitle>
							<VaCardContent>
								<div class="certificate-info">
									<div class="certificate-item">
										<VaIcon name="description" class="certificate-icon" />
										<div class="certificate-content">
											<label class="certificate-label" for="certificate-name">Nombre del certificado</label>
											<span class="certificate-value">{{ certificado?.certificate.subject.attributes[0].value }}</span>
										</div>
									</div>
									<div class="certificate-item">
										<VaIcon name="fingerprint" class="certificate-icon" />
										<div class="certificate-content">
											<label class="certificate-label" for="certificate-serial">Número de serie</label>
											<span class="certificate-value">{{ certificado?.serialNumber }}</span>
										</div>
									</div>
									<div class="certificate-item">
										<VaIcon name="event" class="certificate-icon" />
										<div class="certificate-content">
											<label class="certificate-label" for="certificate-notBefore">Fecha de emisión</label>
											<span class="certificate-value">{{ formatDate(certificado?.certificate.validity?.notBefore) }}</span>
										</div>
									</div>
									<div class="certificate-item">
										<VaIcon name="schedule" class="certificate-icon" />
										<div class="certificate-content">
											<label class="certificate-label" for="certificate-notAfter">Fecha de expiración</label>
											<span class="certificate-value">{{ formatDate(certificado?.certificate.validity?.notAfter) }}</span>
										</div>
									</div>
								</div>
							</VaCardContent>
						</VaCard>
					</div>

					<!-- Estado de conversión -->
					<div v-if="isUploading" class="conversion-status">
						<VaProgressCircle indeterminate size="large" />
						<p>Convirtiendo archivo a base64...</p>
					</div>
				</div>
			</VaCard>
		</template>
		<!-- Botones de acción -->
		<template #footer>
			<div class="modal-actions">
				<VaButton color="secondary" @click="cancelUpload" :disabled="isUploading"> Cancelar </VaButton>
				<VaButton color="primary" @click="processCertificate" :disabled="!selectedFile || isUploading" :loading="isUploading">
					<VaIcon name="check" class="me-2" />
					Procesar Certificado
				</VaButton>
			</div>
		</template>
	</BaseModal>
	<BaseModal v-model="showSuccessModal" title="Éxito" :test-mode="false" :message="successMessage" size="medium">
		<template #body>
			<div class="success-message">
				<div class="success-header">
					<div class="success-icon">
						<i class="fas fa-check-circle"></i>
					</div>
					<h3 class="success-title">Éxito</h3>
				</div>
				<p class="success-description">{{ successMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.success-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #28a745;
}
.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 2px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInSuccess 0.4s ease-out;
}

.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #ffffff;
}

.success-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.success-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.header-card {
	margin-bottom: 100px;
	background: rgba(240, 238, 238, 0.95);
	backdrop-filter: blur(10px);
	border-radius: 24px;
	border: 2px solid rgba(255, 255, 255, 0.2);
}

.header-content {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.header-title {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.title-wrapper {
	display: flex;
	align-items: center;
	gap: 10px;
}

.header-icon {
	font-size: 14px;
	color: #062342;
	background: #062342;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

.institution-title {
	font-size: 24px;
	font-weight: 800;
	background: #062342;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
	letter-spacing: -0.5px;
}

.header-subtitle {
	font-size: 14px;
	color: #6c757d;
	font-weight: 400;
	margin-left: 20px;
}

.user-profile-container {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.user-profile-card {
	background: #062342;
	color: white;
	border-radius: 20px;
	overflow: hidden;
	position: relative;
	border-left: 5px solid #062342;
	transition: transform 0.3s ease;
}

.user-profile-content {
	padding: 20px;
	position: relative;
	z-index: 1;
}

.avatar-section {
	display: flex;
	align-items: center;
	gap: 20px;
}

.avatar-wrapper {
	position: relative;
}

.user-avatar {
	border: 4px solid rgba(255, 255, 255, 0.3);
	box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
	background: #062342;
	font-size: 20px;
	font-weight: 600;
}

.avatar-status-indicator {
	position: absolute;
	bottom: 5px;
	right: 5px;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	border: 3px solid white;
	background: #dc3545;
	transition: all 0.3s ease;
}

.avatar-status-indicator.online {
	background: #28a745;
	box-shadow: 0 0 10px rgba(40, 167, 69, 0.5);
}

.user-name-section {
	flex: 1;
}

.user-full-name {
	font-size: 18px;
	font-weight: 700;
	margin: 0 0 10px 0;
	color: black;
	text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.user-email {
	font-size: 14px;
	opacity: 0.95;
	margin: 0 0 10px 0;
	color: black;
	display: flex;
	align-items: center;
	gap: 5px;
}

.email-icon {
	font-size: 14px;
}

.user-role {
	display: flex;
	align-items: center;
	gap: 5px;
	font-size: 14px;
	opacity: 0.9;
	color: black;
}

.role-icon {
	font-size: 14px;
}

.user-details-card,
.certificate-card {
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(10px);
	border-radius: 20px;
	border: 1px solid rgba(255, 255, 255, 0.2);
	transition:
		transform 0.3s ease,
		box-shadow 0.3s ease;
}

.card-title {
	display: flex;
	align-items: center;
	gap: 1rem;
	font-size: 14px;
	font-weight: 700;
	color: #1a2a3a;
	padding: 20px 20px 0 20px;
}

.title-content {
	display: flex;
	align-items: center;
	gap: 10px;
}

.title-icon {
	color: #062342;
	font-size: 15px;
	background: #062342;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

.details-grid {
	display: flex;
	flex-direction: column;
	gap: 15px;
	padding: 15px 0;
}

.detail-item {
	display: flex;
	align-items: center;
	gap: 15px;
	padding: 15px;
	background: #f8f9fa;
	border-radius: 16px;
	border-left: 5px solid #062342;
	transition: all 0.3s ease;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.detail-item:hover {
	transform: translateX(5px);
	box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.detail-icon-wrapper {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 50px;
	height: 50px;
	background: #062342;
	border-radius: 12px;
}

.detail-icon {
	color: white;
	font-size: 15px;
}

.detail-content {
	flex: 1;
}

.detail-label {
	display: block;
	font-size: 14px;
	font-weight: 600;
	color: #6c757d;
	margin-bottom: 5px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.detail-value {
	display: block;
	font-size: 16px;
	font-weight: 700;
	color: #1a2a3a;
}

.certificate-info {
	display: flex;
	flex-direction: column;
	gap: 15px;
	padding: 15px 0;
}

.certificate-item {
	display: flex;
	align-items: center;
	gap: 15px;
	padding: 15px;
	background: #f8f9fa;
	border-radius: 16px;
	border-left: 5px solid #28a745;
	transition: all 0.3s ease;
}

.certificate-item:hover {
	transform: translateX(5px);
	box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.certificate-icon-wrapper {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 50px;
	height: 50px;
	background: #28a745;
	border-radius: 12px;
	box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.certificate-icon {
	color: white;
	font-size: 15px;
}

.certificate-content {
	flex: 1;
}

.certificate-label {
	display: block;
	font-size: 14px;
	font-weight: 600;
	color: #6c757d;
	margin-bottom: 5px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.certificate-value {
	display: block;
	font-size: 16px;
	font-weight: 700;
	color: #1a2a3a;
	word-break: break-all;
}

/* Estilos para las alertas de certificado */
.expiry-alert {
	margin-bottom: 20px;
	border-radius: 20px;
	overflow: hidden;
	transition:
		transform 0.3s ease,
		box-shadow 0.3s ease;
	position: relative;
}

.expiry-alert.expired {
	background: #ff6b6b;
	border-left: 6px solid #dc3545;
}

.expiry-alert.warning {
	background: #ffa726;
	border-left: 6px solid #ff9800;
}

.expiry-alert.valid {
	background: #66bb6a;
	border-left: 6px solid #4caf50;
}

.alert-content {
	display: flex;
	align-items: center;
	gap: 20px;
	padding: 20px;
	color: white;
	position: relative;
}

.alert-content::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	pointer-events: none;
}

.alert-icon-wrapper {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 50px;
	height: 50px;
	background: rgba(255, 255, 255, 0.2);
	border-radius: 16px;
	backdrop-filter: blur(10px);
	position: relative;
	z-index: 1;
}

.alert-icon {
	color: black;
	font-size: 20px;
}

.alert-text {
	flex: 1;
	position: relative;
	z-index: 1;
}

.alert-title {
	font-size: 14px;
	font-weight: 700;
	margin: 0 0 10px 0;
	color: black;
	text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.alert-message {
	font-size: 16px;
	margin: 0;
	opacity: 0.95;
	color: black;
	line-height: 1.6;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.alert-action {
	flex-shrink: 0;
	position: relative;
	z-index: 1;
}

.action-button {
	border-radius: 12px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
	transition: all 0.3s ease;
}

.action-button:hover {
	transform: translateY(-2px);
	box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
}

/* Estilos para el modal de subida */
.modal-card {
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(10px);
	border-radius: 20px;
	border: 1px solid rgba(255, 255, 255, 0.2);
}

.upload-container {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.file-input-section {
	margin-bottom: 15px;
}

.file-info {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px;
	background: #f8f9fa;
	border-radius: 12px;
	margin-top: 10px;
}

.file-icon {
	color: #062342;
	font-size: 20px;
}

.conversion-status {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 15px;
	padding: 30px;
	background: #f8f9fa;
	border-radius: 16px;
	border: 2px dashed #062342;
}

.conversion-status p {
	margin: 0;
	color: #6c757d;
	font-weight: 600;
	font-size: 16px;
}

.modal-actions {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	padding: 15px 0;
}

/* Responsive design */
@media (max-width: 768px) {
	.avatar-section {
		flex-direction: column;
		text-align: center;
		gap: 15px;
	}

	.user-profile-content {
		padding: 20px;
	}

	.card-title {
		padding: 15px 15px 0 15px;
		font-size: 12px;
	}

	.detail-item,
	.certificate-item {
		padding: 10px;
		gap: 10px;
	}

	.detail-icon-wrapper,
	.certificate-icon-wrapper {
		width: 40px;
		height: 40px;
	}

	.detail-value,
	.certificate-value {
		font-size: 14px;
	}

	.alert-content {
		flex-direction: column;
		text-align: center;
		gap: 15px;
		padding: 15px;
	}

	.alert-title {
		font-size: 12px;
	}

	.alert-message {
		font-size: 14px;
	}

	.modal-actions {
		flex-direction: column;
	}

	.file-info {
		flex-direction: column;
		text-align: center;
	}

	.conversion-status {
		padding: 20px;
	}

	.institution-title {
		font-size: 20px;
	}

	.header-subtitle {
		margin-left: 0;
		font-size: 14px;
	}
}

.va-input__error {
	color: #dc3545;
	font-size: 14px;
	margin-top: 5px;
	font-weight: 600;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.user-profile-card,
.user-details-card,
.certificate-card,
.expiry-alert {
	animation: fadeInUp 0.6s ease-out;
}

.user-details-card {
	animation-delay: 0.2s;
}

.certificate-card {
	animation-delay: 0.3s;
}
</style>
